import { useEffect, useRef, useState } from "react";
import Layout from "../components/Layout";
import ShareButton from "../components/ShareButton";
import DownloadImageButton from "../components/DownloadImageButton";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, RefreshCw } from "lucide-react";

const COLOR = "#EC4899";

const EmojiBattle = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const cardRef = useRef(null);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post("/generate/emoji-battle", {});
      setData(res.data);
      setShowHint(false);
    } catch {
      toast.error("La IA está distraída. Intenta otra vez.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  const emojis = data?.data?.emojis || "";
  const pista = data?.data?.pista;
  const reto = data?.data?.reto;

  return (
    <Layout accent={COLOR}>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16">
        <div className="mb-8">
          <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold bg-[#EC4899] text-white border-4 border-[#1a1a1a] rounded-full px-4 py-1 mb-4">
            tool · 03
          </span>
          <h1 className="font-display font-black text-5xl sm:text-7xl leading-[0.95] tracking-tight mb-4">
            emoji <span className="text-[#EC4899]">battle</span>.
          </h1>
          <p className="font-mono text-sm sm:text-base text-[#4a4a4a] max-w-xl">
            La IA cuenta una mini-historia con emojis. Tu trabajo: descífrala y plásmala en una viñeta o un párrafo.
          </p>
        </div>

        {loading && !data && (
          <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-10 text-center font-mono text-sm uppercase tracking-[0.25em]">
            Convocando emojis...
          </div>
        )}

        {data && (
          <div ref={cardRef} className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-6 sm:p-12 pop-in relative" data-testid="emoji-card">
            <div className="absolute top-3 right-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373] font-bold">
              guango · emoji battle
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-4 text-[#737373]">tu historia</div>
            <div
              data-testid="emoji-sequence"
              className="font-display text-[18vw] sm:text-[12vw] md:text-[8rem] leading-none tracking-wider mb-8 text-center break-words"
              style={{ fontFamily: "'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', 'Bricolage Grotesque', sans-serif" }}
            >
              {emojis}
            </div>

            {reto && (
              <div className="bg-[#EC4899] border-4 border-[#1a1a1a] rounded-2xl px-5 py-4 inline-block font-display font-black text-lg sm:text-2xl mb-6 tilt-2 max-w-full">
                {reto}
              </div>
            )}

            <div className="flex flex-wrap gap-3 items-center mb-6" data-exclude-from-image="true">
              <button
                data-testid="toggle-hint-btn"
                onClick={() => setShowHint((s) => !s)}
                className="press inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-5 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
              >
                {showHint ? <EyeOff className="w-4 h-4" strokeWidth={3} /> : <Eye className="w-4 h-4" strokeWidth={3} />}
                {showHint ? "ocultar pista" : "ver pista"}
              </button>
              <button
                data-testid="emoji-regenerate-btn"
                onClick={generate}
                disabled={loading}
                className="press inline-flex items-center gap-2 bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-xl px-5 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={3} />
                otra historia
              </button>
              <ShareButton promptId={data.id} color={COLOR} testId="emoji-battle-share" />
              <DownloadImageButton
                targetRef={cardRef}
                fileName={`guango-emoji-battle-${data.id.slice(0, 8)}.png`}
                color={COLOR}
                testId="emoji-battle-download"
              />
            </div>

            {showHint && pista && (
              <div data-testid="emoji-hint" className="bg-[#fff7e6] border-4 border-[#1a1a1a] rounded-2xl px-5 py-4 font-mono text-sm sm:text-base">
                💭 {pista}
              </div>
            )}
          </div>
        )}
      </section>
    </Layout>
  );
};

export default EmojiBattle;
